import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Search, Sun, Moon, ChevronDown } from "lucide-react";
import { useCart } from "../../lib/cart";
import { useTheme } from "../../lib/theme";
import VehicleSearch from "./VehicleSearch";
import { BUSINESS } from "../../lib/business";
import { Phone } from "lucide-react";

const SHOP_LINKS = [
  { label: "Shop All", to: "/shop" },
  { label: "Side Mirrors", to: "/shop/side-mirror" },
  { label: "Windshields", to: "/shop/windshield" },
  { label: "Backglass", to: "/shop/backglass" },
  { label: "Sunroof", to: "/shop/sunroof" },
];
const TOP_NAV = [
  { label: "EV Glass", to: "/ev-glass" },
  { label: "Brands", to: "/brands" },
  { label: "Resources", to: "/resources" },
  { label: "About", to: "/about" },
];

export const Header = () => {
  const { count, setOpen } = useCart();
  const { theme, toggle } = useTheme();
  const [mobile, setMobile] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();

  return (
    <header className="sticky top-0 z-40 bg-[rgba(5,5,5,0.7)] backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2">
          <span className="font-display text-xl md:text-2xl font-bold tracking-tighter">
            LECIS<span className="text-red-600">.</span>
          </span>
          <span className="hidden md:block text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-l border-zinc-700 pl-3">
            Auto Glass
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          <ShopMenu loc={loc} />
          {TOP_NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replaceAll("/", "-")}`}
              className={`text-sm tracking-wide link-underline transition-colors ${loc.pathname === n.to ? "text-white" : "text-zinc-400 hover:text-white"}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phoneHref}`}
            data-testid="header-phone"
            className="hidden lg:inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition mr-1"
            title="Call Lecis Auto Glass"
          >
            <Phone size={15} className="text-red-500" /> {BUSINESS.phoneDisplay}
          </a>
          <button
            onClick={() => setSearchOpen((v) => !v)}
            data-testid="header-search-btn"
            aria-label="Search your vehicle"
            aria-expanded={searchOpen}
            className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition ${searchOpen ? "text-red-500 bg-white/5" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
          >
            <Search size={18} />
          </button>
          <button
            data-testid="header-theme-toggle"
            onClick={toggle}
            title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            aria-label="Toggle color theme"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 transition"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            data-testid="header-cart-btn"
            onClick={() => setOpen(true)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full text-zinc-300 hover:text-white hover:bg-white/5 transition"
          >
            <ShoppingBag size={18} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 text-[10px] font-bold bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="header-mobile-toggle"
            onClick={() => setMobile(!mobile)}
            className="lg:hidden text-zinc-300"
          >
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-white/5 bg-[rgba(5,5,5,0.95)] backdrop-blur-xl" data-testid="header-search-panel">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 py-4">
            <VehicleSearch autoFocus onNavigate={() => setSearchOpen(false)} />
            <p className="mt-2 text-[11px] text-zinc-500">
              Find exact-fit side mirrors &amp; windshields by your year, make &amp; model.
            </p>
          </div>
        </div>
      )}

      {mobile && (
        <div className="lg:hidden border-t border-white/5 bg-[#070707]">
          <div className="px-6 py-4 flex flex-col gap-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Shop</div>
            {SHOP_LINKS.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.to.replaceAll("/", "-")}`}
                onClick={() => setMobile(false)}
                className="text-zinc-300 text-base pl-3"
              >
                {n.label}
              </Link>
            ))}
            <div className="border-t border-white/5 mt-1 pt-3 flex flex-col gap-3">
              {TOP_NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.to.replaceAll("/", "-")}`}
                  onClick={() => setMobile(false)}
                  className="text-zinc-300 text-base"
                >
                  {n.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

function ShopMenu({ loc }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);
  const active = loc.pathname.startsWith("/shop");
  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid="nav-shop-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 text-sm tracking-wide link-underline transition-colors ${active ? "text-white" : "text-zinc-400 hover:text-white"}`}
      >
        Shop <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="nav-shop-menu" className="absolute left-0 top-full pt-3 w-52">
          <div className="bg-[rgba(5,5,5,0.95)] backdrop-blur-xl border border-white/10 rounded-lg p-2 shadow-2xl">
            {SHOP_LINKS.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-shop-${n.to.replaceAll("/", "-")}`}
                onClick={() => setOpen(false)}
                className={`block px-3 py-2 rounded-md text-sm transition-colors ${loc.pathname === n.to ? "text-white bg-white/5" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
              >
                {n.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const Footer = () => {
  return (
    <footer className="border-t border-white/5 mt-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
        <div className="col-span-2 md:col-span-3 lg:col-span-2">
          <div className="font-display text-2xl font-bold tracking-tighter mb-3">
            LECIS<span className="text-red-600">.</span>
          </div>
          <p className="text-sm text-zinc-500 max-w-sm leading-relaxed">
            Premium side mirrors and windshields for exotic vehicles and select US brands. Shipped nationwide from Fair Oaks, California.
          </p>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Shop</div>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li><Link to="/shop/side-mirror" className="hover:text-white">Side Mirrors</Link></li>
            <li><Link to="/shop/windshield" className="hover:text-white">Windshields</Link></li>
            <li><Link to="/shop/sunroof" className="hover:text-white" data-testid="footer-sunroof">Sunroof</Link></li>
            <li><Link to="/shop/backglass" className="hover:text-white" data-testid="footer-backglass">Backglass</Link></li>
            <li><Link to="/brands" className="hover:text-white">All Brands</Link></li>
            <li><a href="https://www.ebay.com/str/lecisautoglass" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="footer-ebay">Visit our eBay Store</a></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Customer Service</div>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li><Link to="/about" className="hover:text-white" data-testid="footer-about-link">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-white" data-testid="footer-contact-link">Contact &amp; Fitment Help</Link></li>
            <li><Link to="/resources" className="hover:text-white" data-testid="footer-resources-link">Auto Glass Resources</Link></li>
            <li><Link to="/faq" className="hover:text-white" data-testid="footer-faq-link">FAQs</Link></li>
            <li><Link to="/shipping-policy" className="hover:text-white" data-testid="footer-shipping-policy">Shipping Policy</Link></li>
            <li><Link to="/return-policy" className="hover:text-white" data-testid="footer-return-policy">Return Policy</Link></li>
            <li><Link to="/refund-policy" className="hover:text-white" data-testid="footer-refund-policy">Refund Policy</Link></li>
            <li><Link to="/warranty-policy" className="hover:text-white" data-testid="footer-warranty-policy">Warranty Policy</Link></li>
            <li><Link to="/payment-options" className="hover:text-white" data-testid="footer-payment-options">Payment Options</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Legal</div>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li><Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy-policy">Privacy Policy</Link></li>
            <li><Link to="/terms-conditions" className="hover:text-white" data-testid="footer-terms-conditions">Terms &amp; Conditions</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Lecis Auto Glass</div>
          <ul className="space-y-2 text-sm text-zinc-300" data-testid="footer-nap">
            <li data-testid="footer-address">
              {BUSINESS.address.line1}<br />
              {BUSINESS.address.city}, {BUSINESS.address.region} {BUSINESS.address.postalCode}
            </li>
            <li><a href={`tel:${BUSINESS.phoneHref}`} className="hover:text-white" data-testid="footer-phone">{BUSINESS.phoneDisplay}</a></li>
            <li><a href={`mailto:${BUSINESS.email}`} className="hover:text-white" data-testid="footer-email">{BUSINESS.email}</a></li>
            <li><a href={`mailto:${BUSINESS.infoEmail}`} className="hover:text-white" data-testid="footer-info-email">{BUSINESS.infoEmail}</a></li>
            <li className="text-zinc-500 pt-1" data-testid="footer-hours">{BUSINESS.hoursDisplay}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5 py-6 text-center text-xs text-zinc-600">
        <p className="mb-1" data-testid="footer-payment-trust">Secure payments through Stripe · Major cards · Digital wallets · US bank payments</p>
        © {new Date().getFullYear()} Lecis Auto Glass. All rights reserved.
      </div>
    </footer>
  );
};

export default function StorefrontLayout({ children }) {
  return (
    <div className="storefront min-h-screen flex flex-col bg-[#050505] text-zinc-100">
      <Header />
      <main className="flex-1">{children}</main>
      <CartDrawerLazy />
      <FloatingContact />
      <Footer />
    </div>
  );
}

// Lazy import to avoid circular: cart drawer
import CartDrawer from "./CartDrawer";
import FloatingContact from "./FloatingContact";
function CartDrawerLazy() { return <CartDrawer />; }
